import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { recordAuthEvent } from "@/lib/audit-log.functions";
import { resolveAdminLoginEmail } from "@/lib/admin-login.functions";
import { redeemRecoveryCode } from "@/lib/mfa-recovery.functions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, KeyRound, LifeBuoy, Mail, ShieldCheck } from "lucide-react";
import logoWhiteImg from "@/assets/logo-white.svg";
import { COPYRIGHT_LINE } from "@/config/contact";

type AuthMethod = "password" | "magiclink";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Log ind - AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LoginPage,
});

type Mode = "login" | "mfa" | "recovery";

function LoginPage() {
  const navigate = useNavigate();
  const recordEvent = useServerFn(recordAuthEvent);
  const resolveLoginEmail = useServerFn(resolveAdminLoginEmail);
  const redeemRecovery = useServerFn(redeemRecoveryCode);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [authMethod, setAuthMethod] = useState<AuthMethod>("password");
  const [forgotMode, setForgotMode] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>("login");

  const [mfaCode, setMfaCode] = useState("");
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [recoveryCode, setRecoveryCode] = useState("");
  // Cache email/password så vi kan kalde redeemRecoveryCode efter password er ryddet
  const [cachedEmail, setCachedEmail] = useState<string | null>(null);
  const [cachedPassword, setCachedPassword] = useState<string | null>(null);
  const processedAccessTokenRef = useRef<string | null>(null);

  const resetAndGoToLogin = async () => {
    await supabase.auth.signOut();
    processedAccessTokenRef.current = null;
    setMode("login");
    setMfaCode("");
    setMfaFactorId(null);
    setMfaChallengeId(null);
    setRecoveryCode("");
    setCachedEmail(null);
    setCachedPassword(null);
    setLoading(false);
    setError("");
  };

  const processSignedInSession = async (accessToken: string) => {
    if (processedAccessTokenRef.current === accessToken) return;
    processedAccessTokenRef.current = accessToken;
    setError("");
    setSuccess("");
    setLoading(true);

    const [aalRes, factorRes] = await Promise.all([
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      supabase.auth.mfa.listFactors(),
    ]);

    if (aalRes.error) {
      setError("Kunne ikke verificere session-niveau");
      setLoading(false);
      processedAccessTokenRef.current = null;
      return;
    }

    if (factorRes.error) {
      setError("Kunne ikke hente MFA-faktorer");
      setLoading(false);
      processedAccessTokenRef.current = null;
      return;
    }

    const verifiedFactor = (factorRes.data?.totp ?? []).find((factor) => factor.status === "verified");

    if (verifiedFactor && aalRes.data?.currentLevel !== "aal2") {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: verifiedFactor.id,
      });

      if (challengeError || !challengeData) {
        setError(challengeError?.message || "Kunne ikke starte MFA-verifikation");
        setLoading(false);
        processedAccessTokenRef.current = null;
        return;
      }

      setMfaFactorId(verifiedFactor.id);
      setMfaChallengeId(challengeData.id);
      setMode("mfa");
      setLoading(false);
      return;
    }

    try {
      const description = authMethod === "password" ? "Login med adgangskode" : "Login med magic link";
      await recordEvent({ data: { eventType: "login_success", description } });
    } catch {
      // audit-log er best-effort
    }

    navigate({ to: "/admin" });
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) {
        void processSignedInSession(session.access_token);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.access_token) {
        void processSignedInSession(session.access_token);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const { email } = await resolveLoginEmail({ data: { identifier } });

      // "Glemt adgangskode": send recovery-mail uanset om brugeren findes (vi
      // afslører det ikke for at undgå user-enumeration).
      if (forgotMode) {
        if (email) {
          const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/reset-password`,
          });
          if (resetError) {
            setError("Kunne ikke sende nulstillingslink. Prøv igen.");
            setLoading(false);
            return;
          }
        }
        setSuccess("Hvis brugeren findes, har vi sendt et nulstillingslink til den tilknyttede mail.");
        setLoading(false);
        return;
      }

      // Adgangskode-login: kræver at vi kunne resolve identifier til en email.
      if (authMethod === "password") {
        if (!email) {
          // Generisk fejl for at undgå user-enumeration
          setError("Forkert brugernavn eller adgangskode");
          setLoading(false);
          return;
        }
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) {
          try {
            await recordEvent({ data: { eventType: "login_failed", description: signInError.message } });
          } catch {
            // best-effort
          }
          setError("Forkert brugernavn eller adgangskode");
          setLoading(false);
          return;
        }
        // Cache for evt. recovery-code fallback senere i flowet
        setCachedEmail(email);
        setCachedPassword(password);
        // onAuthStateChange tager over og kalder processSignedInSession.
        return;
      }

      // Magic link fallback
      if (email) {
        const { error: otpError } = await supabase.auth.signInWithOtp({
          email,
          options: {
            emailRedirectTo: `${window.location.origin}/login`,
            shouldCreateUser: false,
          },
        });

        if (otpError) {
          setError("Kunne ikke sende loginlink. Prøv igen.");
          setLoading(false);
          return;
        }
      }

      setSuccess("Hvis brugeren findes, har vi sendt et magic link til den tilknyttede mail.");
    } catch {
      setError("Kunne ikke starte login. Prøv igen.");
    }

    setLoading(false);
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!mfaFactorId || !mfaChallengeId) {
      setError("MFA-session mangler — prøv loginlinket igen");
      return;
    }

    if (!/^\d{6}$/.test(mfaCode.trim())) {
      setError("Indtast den 6-cifrede kode fra din authenticator-app");
      return;
    }

    setLoading(true);
    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId: mfaFactorId,
      challengeId: mfaChallengeId,
      code: mfaCode.trim(),
    });
    setLoading(false);

    if (verifyError) {
      try {
        await recordEvent({ data: { eventType: "mfa_challenge_failed", description: verifyError.message } });
      } catch {
        // best-effort
      }
      setError(
        verifyError.message === "Invalid TOTP code entered"
          ? "Forkert kode — prøv igen med en frisk kode"
          : verifyError.message || "MFA-verifikation mislykkedes",
      );
      return;
    }

    try {
      await recordEvent({ data: { eventType: "mfa_challenge_success", description: "MFA-verifikation gennemført" } });
      await recordEvent({ data: { eventType: "login_success", description: "Login med magic link og MFA" } });
    } catch {
      // best-effort
    }

    navigate({ to: "/admin" });
  };

  const handleRecoveryVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!cachedEmail || !cachedPassword) {
      setError("Session mangler — log ind igen med adgangskode");
      return;
    }
    if (!recoveryCode.trim()) {
      setError("Indtast din recovery-kode");
      return;
    }

    setLoading(true);
    try {
      // Sign out af den nuværende AAL1-session så setSession ikke er en no-op
      await supabase.auth.signOut();
      processedAccessTokenRef.current = null;

      const result = await redeemRecovery({
        data: {
          email: cachedEmail,
          password: cachedPassword,
          code: recoveryCode.trim(),
        },
      });

      // Installer den nye AAL2-session
      const { error: setErr } = await supabase.auth.setSession({
        access_token: result.access_token,
        refresh_token: result.refresh_token,
      });
      if (setErr) throw new Error(setErr.message);

      // Markér token som processed så onAuthStateChange ikke trigger MFA-flow igen
      processedAccessTokenRef.current = result.access_token;

      try {
        await recordEvent({ data: { eventType: "login_success", description: "Login med recovery-kode" } });
      } catch {
        // best-effort
      }

      const remaining = result.remaining ?? 0;
      // Toast med antal resterende koder
      if (remaining === 0) {
        toast.error("Du har ingen recovery-koder tilbage", {
          description: "Generér nye koder fra din profil med det samme.",
          duration: 8000,
        });
      } else if (remaining <= 2) {
        toast.warning(`Kun ${remaining} recovery-${remaining === 1 ? "kode" : "koder"} tilbage`, {
          description: "Generér nye koder fra din profil for at undgå at blive låst ude.",
          duration: 8000,
        });
      } else {
        toast.success(`Recovery-kode brugt — du har ${remaining} koder tilbage`, {
          duration: 5000,
        });
      }

      navigate({ to: "/admin" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Kunne ikke verificere recovery-kode";
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-dark-blue px-4 pt-0">
      <div className="w-full max-w-sm">
        <div className="text-center">
          <Link to="/"><img src={logoWhiteImg} alt="AutoXChange" className="mx-auto h-12 w-auto" /></Link>
          <p className="mt-2 text-sm text-white/70">
            {mode === "login" && !forgotMode && "Log ind med brugernavn eller e-mail"}
            {mode === "login" && forgotMode && "Nulstil din adgangskode"}
            {mode === "mfa" && "To-faktor godkendelse"}
            {mode === "recovery" && "Brug en recovery-kode"}
          </p>
        </div>

        {mode === "login" && (
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            {error && (
              <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
            )}
            {success && (
              <div className="rounded-lg bg-green-500/10 p-3 text-sm text-green-400">{success}</div>
            )}
            <Input
              type="text"
              placeholder="Brugernavn eller e-mail"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoFocus
              autoComplete="username"
              className="border-white/15 bg-white/5 text-white placeholder:text-white/60"
            />
            {!forgotMode && authMethod === "password" && (
              <Input
                type="password"
                placeholder="Adgangskode"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className="border-white/15 bg-white/5 text-white placeholder:text-white/60"
              />
            )}
            <Button type="submit" disabled={loading} className="w-full bg-accent text-accent-foreground hover:brightness-110">
              {forgotMode ? (
                <><Mail size={16} className="mr-2" /> {loading ? "Sender..." : "Send nulstillingslink"}</>
              ) : authMethod === "password" ? (
                <><KeyRound size={16} className="mr-2" /> {loading ? "Logger ind..." : "Log ind"}</>
              ) : (
                <><Mail size={16} className="mr-2" /> {loading ? "Sender..." : "Send magic link"}</>
              )}
            </Button>

            {!forgotMode && (
              <div className="flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => {
                    setAuthMethod((m) => (m === "password" ? "magiclink" : "password"));
                    setError("");
                    setSuccess("");
                    setPassword("");
                  }}
                  className="text-white/50 transition-colors hover:text-white/80"
                >
                  {authMethod === "password" ? "Brug magic link i stedet" : "Brug adgangskode i stedet"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForgotMode(true);
                    setError("");
                    setSuccess("");
                    setPassword("");
                  }}
                  className="text-white/50 transition-colors hover:text-white/80"
                >
                  Glemt adgangskode?
                </button>
              </div>
            )}

            {forgotMode && (
              <button
                type="button"
                onClick={() => {
                  setForgotMode(false);
                  setError("");
                  setSuccess("");
                }}
                className="flex w-full items-center justify-center gap-1 text-sm text-white/70 transition-colors hover:text-white/70"
              >
                <ArrowLeft size={14} /> Tilbage til login
              </button>
            )}

            {!forgotMode && authMethod === "magiclink" && (
              <p className="text-center text-sm text-white/70">
                Vi sender et engangslink til din mail, så du kan logge ind uden adgangskode.
              </p>
            )}

            {success && authMethod === "magiclink" && !forgotMode && (
              <div className="space-y-2 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/60">
                <p>
                  <span className="font-medium text-white/80">Linket er gyldigt i 60 minutter</span> og kan kun bruges én gang.
                </p>
                <p className="font-medium text-white/80">Får du ikke mailen?</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>Tjek dit spam- eller uønsket-post-filter</li>
                  <li>Bekræft at brugernavnet eller mailadressen er korrekt stavet</li>
                  <li>Vent 1-2 minutter — leveringen kan være forsinket</li>
                  <li>Prøv at sende et nyt link nedenfor</li>
                </ul>
              </div>
            )}
          </form>
        )}

        {mode === "mfa" && (
          <form onSubmit={handleMfaVerify} className="mt-8 space-y-4">
            <div className="rounded-lg bg-white/5 p-3 text-center text-sm text-white/60">
              <ShieldCheck size={20} className="mx-auto mb-1 text-accent" />
              Indtast den 6-cifrede kode fra din authenticator-app
            </div>
            {error && (
              <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
            )}
            <Input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              placeholder="123456"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
              required
              autoFocus
              className="border-white/15 bg-white/5 text-center text-lg font-mono tracking-widest text-white placeholder:text-white/60"
            />
            <Button
              type="submit"
              disabled={loading || mfaCode.length !== 6}
              className="w-full bg-accent text-accent-foreground hover:brightness-110"
            >
              <ShieldCheck size={16} className="mr-2" /> {loading ? "Verificerer..." : "Verificér"}
            </Button>
            {cachedEmail && cachedPassword && (
              <button
                type="button"
                onClick={() => {
                  setError("");
                  setMfaCode("");
                  setMode("recovery");
                }}
                className="block w-full text-center text-xs text-white/60 underline-offset-2 hover:text-white hover:underline"
              >
                Mistet din authenticator? Brug en recovery-kode
              </button>
            )}
            <button
              type="button"
              onClick={resetAndGoToLogin}
              className="flex w-full items-center justify-center gap-1 text-sm text-white/70 transition-colors hover:text-white/70"
            >
              <ArrowLeft size={14} /> Tilbage til login
            </button>
          </form>
        )}

        {mode === "recovery" && (
          <form onSubmit={handleRecoveryVerify} className="mt-8 space-y-4">
            <div className="rounded-lg bg-white/5 p-3 text-center text-sm text-white/60">
              <LifeBuoy size={20} className="mx-auto mb-1 text-accent" />
              Indtast en af de recovery-koder du gemte da du satte to-faktor godkendelse op. Hver kode
              kan kun bruges én gang.
            </div>
            {error && (
              <div className="rounded-lg bg-red-500/10 p-3 text-sm text-red-400">{error}</div>
            )}
            <Input
              type="text"
              autoComplete="one-time-code"
              placeholder="XXXX-XXXX-XX"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
              required
              autoFocus
              maxLength={20}
              className="border-white/15 bg-white/5 text-center font-mono tracking-widest text-white placeholder:text-white/60"
            />
            <Button
              type="submit"
              disabled={loading || !recoveryCode.trim()}
              className="w-full bg-accent text-accent-foreground hover:brightness-110"
            >
              <LifeBuoy size={16} className="mr-2" />{" "}
              {loading ? "Verificerer..." : "Log ind med recovery-kode"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setError("");
                setRecoveryCode("");
                setMode("mfa");
              }}
              className="block w-full text-center text-xs text-white/60 underline-offset-2 hover:text-white hover:underline"
            >
              Tilbage til authenticator-kode
            </button>
            <button
              type="button"
              onClick={resetAndGoToLogin}
              className="flex w-full items-center justify-center gap-1 text-sm text-white/70 transition-colors hover:text-white/70"
            >
              <ArrowLeft size={14} /> Tilbage til login
            </button>
          </form>
        )}
      </div>

      <div className="absolute bottom-6 left-0 right-0 text-center text-xs text-white/60">
        {COPYRIGHT_LINE(2026)}
      </div>
    </div>
  );
}
